import { toast } from 'sonner';
import { useMutation } from '@apollo/client/react';

import { useOnboardingStepSubmit, useStepSubmitHandler } from 'src/context/onboarding-step-submit';
import { AgreementConditionEnum, LegalContextEnum, type IAgreementUpdateParams } from 'src/graphql/schema';
import { AgreementUpdateDocument } from 'src/graphql/actions/agreement/agreementUpdate.mutation';
import { ClientError, getApiError } from 'src/utils/errors';

type IAgreementSelection = Pick<
  IAgreementUpdateParams,
  'legalContext' | 'legalContextOther' | 'conditions' | 'conditionsOther'
>;

type Props = {
  escrowId: string;
  agreement: IAgreementSelection;
  missingSelectionError: string;
  missingOtherError: string;
};

const hasLegalContextAndCondition = ({ legalContext, conditions }: IAgreementSelection) =>
  Boolean(legalContext?.length && conditions?.length);

const hasLegalContextOther = ({ legalContext, legalContextOther }: IAgreementSelection) =>
  !legalContext?.includes(LegalContextEnum.Other) || Boolean(legalContextOther?.trim());

const hasConditionsOther = ({ conditions, conditionsOther }: IAgreementSelection) =>
  !conditions?.includes(AgreementConditionEnum.Other) || Boolean(conditionsOther?.trim());

export const useAgreementDetailsStep = ({ escrowId, agreement, missingSelectionError, missingOtherError }: Props) => {
  const [updateAgreement] = useMutation(AgreementUpdateDocument);
  const { stepError, clearStepError } = useOnboardingStepSubmit();

  const getStepError = (selection: IAgreementSelection) => {
    if (!hasLegalContextAndCondition(selection)) return missingSelectionError;
    if (!hasLegalContextOther(selection) || !hasConditionsOther(selection)) return missingOtherError;

    return null;
  };

  const validateAgreementDetails = async () => getStepError(agreement) ?? true;

  useStepSubmitHandler(validateAgreementDetails);

  const onUpdateAgreement = async (agreementParams: IAgreementUpdateParams) => {
    try {
      const { data } = await updateAgreement({ variables: { escrowId, agreementParams } });

      if (!data?.agreementUpdate?.success) throw new ClientError(data?.agreementUpdate?.errors?.[0]);

      toast.success('Saved!');

      if (!getStepError({ ...agreement, ...agreementParams })) clearStepError();
    } catch (error: unknown) {
      getApiError(error, toast.error);
    }
  };

  // The fields are marked only once Next has reported the missing "Other" text, and each clears as soon as it is saved.
  const isMissingOther = stepError === missingOtherError;

  return {
    onUpdateAgreement,
    isLegalContextOtherInvalid: isMissingOther && !hasLegalContextOther(agreement),
    isConditionsOtherInvalid: isMissingOther && !hasConditionsOther(agreement),
  };
};
